import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { useAuth } from '../../shared/auth/AuthContext';
import { useCompanies } from '../organization/useOrganization';

/* ── Types ── */

export interface Account {
  id: string;
  code: string;
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  parentId: string | null;
  balance: number;
  children?: Account[];
}

export interface AccountInput {
  code: string;
  name: string;
  type: Account['type'];
  parentId?: string | null;
}

export interface JournalLine {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
}

export type EntryStatus = 'DRAFT' | 'POSTED' | 'REVERSED' | 'VOID';

export interface JournalEntry {
  id: string;
  /** Numero do lancamento, como o contador o identifica. */
  entryNumber: string;
  date: string;
  description: string;
  type: string;
  status: EntryStatus;
  /* Totais vem do servidor: o lancamento e a unidade contabil e o total dele e
     gravado junto. Somar as linhas no cliente daria outro numero se alguma
     linha nao viesse na resposta. */
  totalDebit: number;
  totalCredit: number;
  source: string;
  sourceId: string | null;
  postedBy: string | null;
  postedAt: string | null;
  lines: JournalLine[];
  createdAt: string;
}

export interface JournalEntryInput {
  date: string;
  description: string;
  lines: { accountId: string; debit: number; credit: number }[];
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/* ── Contexto contábil (tenant da sessão + primeira empresa) ── */

export interface AccountingContext {
  tenantId: string;
  companyId: string | null;
  userId: string | null;
}

export function useAccountingContext(): AccountingContext & { ready: boolean } {
  const { tenant, user } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { data: companies } = useCompanies(tenantId);
  const companyId = companies?.[0]?.id ?? null;
  return { tenantId, companyId, userId: user?.id ?? null, ready: !!tenantId && !!companyId };
}

type Ctx = { tenantId: string; companyId: string };

function ctxParams(ctx: Ctx): Record<string, string> {
  return { tenantId: ctx.tenantId, companyId: ctx.companyId };
}

/**
 * Hook: resolves the accounting context (tenantId + first companyId) from the
 * active session, returning `null` until both are available.
 *
 * Every accounting hook expects `ctx?: Ctx | null` — pass the result of this
 * hook as the first argument so queries stay enabled only when the context is
 * ready (SDD tenancy rules: tenant/company never come from the request body).
 */
export function useAccountingCtx(): Ctx | null {
  const { tenantId, companyId, ready } = useAccountingContext();
  if (!ready || !tenantId || !companyId) return null;
  return { tenantId, companyId };
}

/* ── Mappers (contrato real → tipos da UI) ── */

interface BackendAccount {
  id: string;
  code: string;
  name: string;
  type: string;
  parentId?: string | null;
  balance?: number | null;
  children?: BackendAccount[] | null;
}

function toAccount(raw: BackendAccount): Account {
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    type: raw.type.toLowerCase() as Account['type'],
    parentId: raw.parentId ?? null,
    balance: raw.balance ?? 0,
    children: raw.children?.map(toAccount),
  };
}

function flattenAccounts(roots: Account[]): Account[] {
  const out: Account[] = [];
  const visit = (nodes: Account[]): void => {
    for (const node of nodes) {
      out.push(node);
      if (node.children) visit(node.children);
    }
  };
  visit(roots);
  return out;
}

interface BackendJournalLine {
  id: string;
  accountId: string;
  debit: number;
  credit: number;
}

interface BackendJournalEntry {
  id: string;
  entryNumber: string;
  entryDate: string;
  description: string;
  type: string;
  status: string;
  totalDebit: number;
  totalCredit: number;
  source: string;
  sourceId: string | null;
  postedBy: string | null;
  postedAt: string | null;
  lines: BackendJournalLine[];
  createdAt: string;
}

function toJournalEntry(raw: BackendJournalEntry): JournalEntry {
  return {
    id: raw.id,
    entryNumber: raw.entryNumber,
    date: typeof raw.entryDate === 'string' ? raw.entryDate.slice(0, 10) : '',
    description: raw.description,
    type: raw.type,
    status: (raw.status ?? 'POSTED') as EntryStatus,
    totalDebit: raw.totalDebit ?? 0,
    totalCredit: raw.totalCredit ?? 0,
    source: raw.source,
    sourceId: raw.sourceId ?? null,
    postedBy: raw.postedBy ?? null,
    postedAt: raw.postedAt ?? null,
    lines: (raw.lines ?? []).map((l) => ({
      id: l.id,
      accountId: l.accountId,
      accountCode: '',
      accountName: '',
      debit: l.debit,
      credit: l.credit,
    })),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date(raw.createdAt).toISOString(),
  };
}

interface BackendTrialRow {
  accountCode: string;
  accountName: string;
  accountType: string;
  accountNature: string;
  totalDebit: number;
  totalCredit: number;
  netBalance: number;
  /* Campos do balancete por período [dateFrom, date]. O servidor os devolve
     desde que o DTO TrialBalanceRowDto existiu; o hook antigo os ignorava e
     a tela de Balancete não tinha Saldo Inicial nem movimento do período. */
  openingBalance?: number;
  periodDebit?: number;
  periodCredit?: number;
  finalBalance?: number;
}

interface BackendStatementLine {
  accountCode: string;
  accountName: string;
  balance: number;
}

interface BackendIncomeStatement {
  startDate: string;
  endDate: string;
  revenue: BackendStatementLine[];
  expenses: BackendStatementLine[];
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
}

interface BackendBalanceSheet {
  date: string;
  assets: BackendStatementLine[];
  liabilities: BackendStatementLine[];
  equity: BackendStatementLine[];
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  isBalanced: boolean;
}

/** Uma conta analitica e o saldo dela no periodo, na natureza da conta. */
export interface StatementLine {
  accountCode: string;
  accountName: string;
  balance: number;
}

export type AccountNature = 'DEBIT' | 'CREDIT';

export interface TrialBalanceRow {
  code: string;
  name: string;
  type: string;
  /** Natureza da conta: e ela que define o sinal de `balance`. */
  nature: AccountNature;
  debit: number;
  credit: number;
  balance: number;
  /** Saldo inicial (antes de `dateFrom`). 0 quando dateFrom não informado. */
  openingBalance: number;
  /** Movimento de débitos no período [dateFrom, date]. */
  periodDebit: number;
  /** Movimento de créditos no período [dateFrom, date]. */
  periodCredit: number;
  /** Saldo final do período (igual a netBalance). */
  finalBalance: number;
}

export interface TrialBalance {
  accounts: TrialBalanceRow[];
  totalDebit: number;
  totalCredit: number;
}

export interface IncomeStatement {
  startDate: string;
  endDate: string;
  /* Linhas por conta analitica: o servidor sempre as devolveu. Sem elas a tela
     so podia mostrar tres numeros — e inventava a separacao entre custo e
     despesa repetindo `totalExpenses` em duas linhas. */
  revenue: StatementLine[];
  expenses: StatementLine[];
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
}

export interface BalanceSheet {
  date: string;
  assets: StatementLine[];
  liabilities: StatementLine[];
  equity: StatementLine[];
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  /** Conferencia do servidor: Ativo = Passivo + PL. */
  isBalanced: boolean;
}

/* ── Helpers ── */

const ACCOUNT_TYPE_LABEL: Record<Account['type'], string> = {
  asset: 'Ativo',
  liability: 'Passivo',
  equity: 'Patrimonio Liquido',
  revenue: 'Receita',
  expense: 'Despesa',
};

function accountTypeBadgeVariant(
  type: Account['type'],
): 'info' | 'success' | 'warning' | 'error' | 'neutral' {
  const map: Record<Account['type'], 'info' | 'success' | 'warning' | 'error' | 'neutral'> = {
    asset: 'info',
    liability: 'warning',
    equity: 'success',
    revenue: 'neutral',
    expense: 'error',
  };
  return map[type];
}

export { ACCOUNT_TYPE_LABEL, accountTypeBadgeVariant };

/* ── Hooks: Chart of Accounts ── */

export function useChartOfAccounts(ctx?: Ctx | null) {
  return useQuery({
    queryKey: [...queryKeys.accounting.chart(), ctx] as const,
    queryFn: async (): Promise<Account[]> => {
      /* `GET /accounts` NAO EXISTE no backend: o que e servido e
         `GET /accounts/chart`, que devolve a arvore pronta (tenant e empresa
         da sessao). Com o path errado a tela recebia 404 e mostrava
         "Nenhuma conta cadastrada" **sempre** — era uma das lacunas abertas no
         backend todo33 T23, e o que ela quebrava era esta tela inteira. */
      const res = await apiClient.get<BackendAccount[]>('/accounts/chart', {
        params: ctx ? ctxParams(ctx) : {},
      });
      return (Array.isArray(res) ? res : []).map(toAccount);
    },
    enabled: !!ctx,
  });
}

export function useAccounts(ctx?: Ctx | null) {
  const chart = useChartOfAccounts(ctx);
  return {
    ...chart,
    data: chart.data ? flattenAccounts(chart.data) : undefined,
  };
}

export { useChartOfAccounts as useAccountChart };

export function useCreateAccount(ctx?: Ctx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountInput) =>
      apiClient.post<BackendAccount>('/accounts', {
        code: input.code,
        name: input.name,
        type: input.type.toUpperCase(),
        parentId: input.parentId ?? null,
      }, { params: ctx ? ctxParams(ctx) : {} }).then(toAccount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.chart() }).catch(() => {});
    },
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: AccountInput & { id: string }) =>
      apiClient.patch<BackendAccount>(`/accounts/${id}`, {
        code: input.code,
        name: input.name,
        type: input.type.toUpperCase(),
      }).then(toAccount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.chart() }).catch(() => {});
    },
  });
}

export function useDeleteAccount(ctx?: Ctx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/accounts/${id}`, {
        params: ctx ? ctxParams(ctx) : {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.chart() }).catch(() => {});
    },
  });
}

export function useAccountBalance(ctx?: Ctx | null, id?: string) {
  return useQuery({
    queryKey: [...queryKeys.accounting.chart(), 'balance', ctx, id] as const,
    queryFn: () =>
      apiClient.get<{ balance: number }>(`/accounts/${id}/balance`, {
        params: ctx ? ctxParams(ctx) : {},
      }),
    enabled: !!ctx && !!id,
  });
}

/* ── Hooks: Journal Entries ── */

export function useJournalEntries(
  ctx?: Ctx | null,
  params?: { page?: number; limit?: number; startDate?: string; endDate?: string; accountId?: string },
) {
  return useQuery({
    queryKey: [...queryKeys.accounting.entries(), ctx, params] as const,
    queryFn: async (): Promise<PaginatedResult<JournalEntry>> => {
      const res = await apiClient.get<BackendJournalEntry[] | PaginatedResult<BackendJournalEntry>>(
        '/entries',
        {
          params: {
            ...(ctx ? ctxParams(ctx) : {}),
            page: String(params?.page ?? 1),
            limit: String(params?.limit ?? 20),
            ...(params?.startDate ? { dateFrom: params.startDate, startDate: params.startDate } : {}),
            ...(params?.endDate ? { dateTo: params.endDate, endDate: params.endDate } : {}),
            ...(params?.accountId ? { accountId: params.accountId } : {}),
          },
        },
      );
      const items = Array.isArray(res) ? res : (res.data ?? []);
      const page = params?.page ?? 1;
      const limit = params?.limit ?? 20;
      return {
        data: items.map(toJournalEntry),
        total: Array.isArray(res) ? items.length : (res.total ?? items.length),
        page,
        limit,
        totalPages: Math.max(1, Math.ceil((Array.isArray(res) ? items.length : (res.total ?? items.length)) / limit)),
      };
    },
    enabled: !!ctx,
  });
}

export { useJournalEntries as useEntries };

export function useCreateEntry(ctx?: Ctx | null, postedBy?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: JournalEntryInput) =>
      apiClient
        .post<BackendJournalEntry>(
          '/entries',
          {
            entryDate: input.date,
            description: input.description,
            lines: input.lines.map((l) => ({
              accountId: l.accountId,
              debit: l.debit,
              credit: l.credit,
            })),
          },
          {
            params: {
              ...(ctx ? ctxParams(ctx) : {}),
              ...(postedBy ? { postedBy } : {}),
            },
          },
        )
        .then(toJournalEntry),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.entries() }).catch(() => {});
      qc.invalidateQueries({ queryKey: queryKeys.accounting.chart() }).catch(() => {});
    },
  });
}

/* ── Hooks: Financial Statements ── */

export function useTrialBalance(ctx?: Ctx | null, date?: string, dateFrom?: string) {
  return useQuery({
    queryKey: [...queryKeys.accounting.trialBalance(), ctx, date, dateFrom] as const,
    queryFn: async (): Promise<TrialBalance> => {
      const res = await apiClient.get<BackendTrialRow[]>(
        '/trial-balance',
        {
          params: {
            ...(ctx ? ctxParams(ctx) : {}),
            ...(date ? { date } : {}),
            ...(dateFrom ? { dateFrom } : {}),
          },
        },
      );
      const accounts = (Array.isArray(res) ? res : []).map((r) => ({
        code: r.accountCode,
        name: r.accountName,
        type: r.accountType,
        nature: (r.accountNature === 'CREDIT' ? 'CREDIT' : 'DEBIT') as AccountNature,
        debit: r.totalDebit,
        credit: r.totalCredit,
        balance: r.netBalance,
        /* Campos de período: o backend os devolve desde a versão do DTO
           TrialBalanceRowDto. Quando o servidor não os manda (resposta
           antiga), zero — o total acumulado é o que vale. */
        openingBalance: r.openingBalance ?? 0,
        periodDebit: r.periodDebit ?? 0,
        periodCredit: r.periodCredit ?? 0,
        finalBalance: r.finalBalance ?? r.netBalance,
      }));
      /* Aqui somar no cliente e correto: o balancete vem inteiro, sem
         paginacao, e o total dele E a soma das contas listadas. */
      return {
        accounts,
        totalDebit: accounts.reduce((sum, a) => sum + a.debit, 0),
        totalCredit: accounts.reduce((sum, a) => sum + a.credit, 0),
      };
    },
    enabled: !!ctx,
  });
}

export function useIncomeStatement(ctx?: Ctx | null, startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: [...queryKeys.accounting.incomeStatement(), ctx, startDate, endDate] as const,
    queryFn: async (): Promise<IncomeStatement> => {
      const res = await apiClient.get<BackendIncomeStatement>('/income-statement', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
      });
      return {
        startDate: res.startDate ?? '',
        endDate: res.endDate ?? '',
        revenue: res.revenue ?? [],
        expenses: res.expenses ?? [],
        totalRevenue: res.totalRevenue,
        totalExpenses: res.totalExpenses,
        netIncome: res.netIncome,
      };
    },
    enabled: !!ctx && !!startDate && !!endDate,
  });
}

export function useBalanceSheet(ctx?: Ctx | null, date?: string) {
  return useQuery({
    queryKey: [...queryKeys.accounting.balanceSheet(), ctx, date] as const,
    queryFn: async (): Promise<BalanceSheet> => {
      const res = await apiClient.get<BackendBalanceSheet>('/balance-sheet', {
        params: { ...(ctx ? ctxParams(ctx) : {}), ...(date ? { date } : {}) },
      });
      return {
        date: res.date ?? '',
        assets: res.assets ?? [],
        liabilities: res.liabilities ?? [],
        equity: res.equity ?? [],
        totalAssets: res.totalAssets,
        totalLiabilities: res.totalLiabilities,
        totalEquity: res.totalEquity,
        isBalanced: res.isBalanced ?? false,
      };
    },
    enabled: !!ctx,
  });
}

/* ── Hooks: DRE Gerencial / Waterfall (GET /accounting/dre) ── */

export interface DreLine {
  code: string;
  name: string;
  level: number;
  section: 'REVENUE' | 'EXPENSE';
  /** Realizado no período, na natureza da conta (R$). */
  realized: number;
  /** Análise vertical (%). Base: Receita Bruta (receitas) / Receita Líquida (despesas). */
  av: number | null;
  /** Orçado (R$). SEMPRE null nesta versão: não existe módulo de orçamento. */
  orcado: number | null;
  orcadoReason: string | null;
  orcadoStatus: string | null;
  /** Var (%) vs orçado. Sempre null — depende do orçado. */
  var: number | null;
}

export type WaterfallItemType = 'value' | 'deduction' | 'subtotal' | 'total';

export interface WaterfallItem {
  key: string;
  label: string;
  value: number;
  type: WaterfallItemType;
}

export interface DreGerencial {
  startDate: string;
  endDate: string;
  budget: { available: boolean; reason: string };
  avBase: { label: 'receita_bruta' | 'receita_liquida'; value: number | null };
  revenue: DreLine[];
  expenses: DreLine[];
  receitaBruta: number;
  deducoesDaReceita: number;
  receitaLiquida: number;
  totalExpenses: number;
  netIncome: number;
  waterfall?: WaterfallItem[];
}

interface BackendDreGerencial {
  startDate: string;
  endDate: string;
  budget: { available: boolean; reason: string };
  avBase: { label: 'receita_bruta' | 'receita_liquida'; value: number | null };
  revenue: DreLine[];
  expenses: DreLine[];
  receitaBruta: number;
  deducoesDaReceita: number;
  receitaLiquida: number;
  totalExpenses: number;
  netIncome: number;
  waterfall?: WaterfallItem[];
}

/**
 * DRE Gerencial + série waterfall — `GET /accounting/dre?startDate&endDate`.
 * Um único payload serve as duas telas: a Estrutura de Contas (Realizado,
 * AV%, Orçado, Var%) e o gráfico de fluxo de resultado (waterfall).
 */
export function useDreGerencial(ctx?: Ctx | null, startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['accounting', 'dre-gerencial', ctx, startDate, endDate] as const,
    queryFn: async (): Promise<DreGerencial> => {
      const res = await apiClient.get<BackendDreGerencial>('/accounting/dre', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          startDate: startDate ?? '',
          endDate: endDate ?? '',
        },
      });
      return {
        startDate: res.startDate ?? startDate ?? '',
        endDate: res.endDate ?? endDate ?? '',
        budget: res.budget ?? { available: false, reason: '' },
        avBase: res.avBase ?? { label: 'receita_liquida', value: null },
        revenue: res.revenue ?? [],
        expenses: res.expenses ?? [],
        receitaBruta: res.receitaBruta ?? 0,
        deducoesDaReceita: res.deducoesDaReceita ?? 0,
        receitaLiquida: res.receitaLiquida ?? 0,
        totalExpenses: res.totalExpenses ?? 0,
        netIncome: res.netIncome ?? 0,
        waterfall: res.waterfall ?? [],
      };
    },
    enabled: !!ctx && !!startDate && !!endDate,
  });
}

/* ── Hooks: Reconhecimento de Receita (GET /accounting/revenue-recognition) ── */

export type RevenueComplianceStatus = 'IFRS15_ALIGNED' | 'REVIEW_NEEDED' | 'NON_COMPLIANT';

export interface RevenueScheduleRow {
  month: string;
  forecast: number;
  actual: number;
  variance: number;
  contracts: number;
}

export interface RevenueAnomalyRow {
  contractId: string;
  clientName: string | null;
  month: string;
  type: string;
  severity: string;
  message: string;
}

export interface RevenueContractRow {
  id: string;
  contractId: string;
  clientName: string | null;
  recognizedAmount: number;
  complianceStatus: string;
  totalForecast: number;
  totalActual: number;
  variance: number;
  months: RevenueScheduleRow[];
}

export interface RevenueRecognitionDashboard {
  summary: {
    totalContracts: number;
    totalForecast: number;
    totalActual: number;
    totalVariance: number;
    complianceByStatus: Record<string, number>;
  };
  schedule: RevenueScheduleRow[];
  contracts: RevenueContractRow[];
  anomalies: RevenueAnomalyRow[];
  generatedAt: string;
}

/**
 * Reconhecimento de Receita / Diferimento — `GET /accounting/revenue-recognition`.
 * O DTO (RevenueRecognitionDashboardDto) e a query (GetRevenueRecognitionQuery)
 * existem no backend; o controller HTTP ainda não foi registrado (status em
 * out/2026), então a tela deve tratar erro/404 como estado vazio explícito.
 */
export function useRevenueRecognition(
  ctx?: Ctx | null,
  options?: { monthFrom?: string; monthTo?: string; limit?: number },
) {
  return useQuery({
    queryKey: ['accounting', 'revenue-recognition', ctx, options] as const,
    queryFn: async (): Promise<RevenueRecognitionDashboard> => {
      const res = await apiClient.get<RevenueRecognitionDashboard>(
        '/accounting/revenue-recognition',
        {
          params: {
            ...(ctx ? ctxParams(ctx) : {}),
            ...(options?.monthFrom ? { monthFrom: options.monthFrom } : {}),
            ...(options?.monthTo ? { monthTo: options.monthTo } : {}),
            ...(options?.limit ? { limit: String(options.limit) } : {}),
          },
        },
      );
      return {
        summary: res.summary ?? {
          totalContracts: 0,
          totalForecast: 0,
          totalActual: 0,
          totalVariance: 0,
          complianceByStatus: {},
        },
        schedule: res.schedule ?? [],
        contracts: res.contracts ?? [],
        anomalies: res.anomalies ?? [],
        generatedAt: res.generatedAt ?? '',
      };
    },
    enabled: !!ctx,
  });
}
